import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

// Ensure the loader covers full page on reload and can be triggered universally
// Matches the site's black/white aesthetic; locks scroll while visible

type LoaderContextValue = {
  isVisible: boolean;
  isPreloader: boolean;
  message?: string;
  show: (message?: string) => void;
  hide: () => void;
};

const LoaderContext = createContext<LoaderContextValue | null>(null);

export function LoaderProvider({ children }: { children: React.ReactNode }) {
  const hasStatic = typeof document !== 'undefined' && !!document.getElementById('preloader');
  const initialVisible = !hasStatic && (typeof document !== 'undefined' ? document.readyState !== 'complete' : true);
  const [isVisible, setIsVisible] = useState<boolean>(initialVisible);
  const [isPreloader, setIsPreloader] = useState<boolean>(initialVisible);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const startRef = useRef<number | null>(initialVisible ? performance.now() : null);
  const MIN_SHOW_MS = 8000; // minimum visible time to avoid flicker

  useEffect(() => {
    const onLoad = () => {
      const elapsed = startRef.current ? performance.now() - startRef.current : MIN_SHOW_MS;
      const wait = Math.max(0, MIN_SHOW_MS - elapsed);
      setTimeout(() => {
        setIsVisible(false);
        setIsPreloader(false);
        startRef.current = null;
      }, wait);
    };

    // If already loaded, still show briefly to meet UX expectation
    if (document.readyState === 'complete' && isPreloader) {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
    }

    return () => {
      window.removeEventListener('load', onLoad);
    };
  }, [isPreloader]);

  // Lock body scroll while loader is visible
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  const show = (msg?: string) => {
    setMessage(msg);
    setIsPreloader(false);
    setIsVisible(true);
  };

  const hide = () => {
    setIsVisible(false);
    setMessage(undefined);
  };

  const value = useMemo(
    () => ({ isVisible, isPreloader, message, show, hide }),
    [isVisible, isPreloader, message]
  );

  return <LoaderContext.Provider value={value}>{children}</LoaderContext.Provider>;
}

export function useLoader() {
  const ctx = useContext(LoaderContext);
  if (!ctx) throw new Error('useLoader must be used within LoaderProvider');
  return ctx;
}